import { hooks } from '@wove/react';
import { forwardRef, ReactNode, useImperativeHandle, useRef, useState } from 'react';
import { useEffectCustom } from '../hooks';
import { NativeScroll } from '../native-scroll';
import { NativeProps, withNativeProps } from '../utils/native-props';
import { StickyContext } from './context';
import './style.less';

export type StickyProps = {
  // height中number 值为视觉稿高度
  height?: string | number;
  children: ReactNode | ReactNode[];
  onChange?: (activeKey?: string) => void;
  onScroll?: (event) => void;
} & NativeProps;

export type StickyRefApi = {
  scrollToByKey: (activeKey: string, behavior?: ScrollBehavior) => void;
  scrollTo: (top: number, behavior?: ScrollBehavior) => void;
};

/**
 * Sticky
 * ```
 * 1. 子节点必须是StickyItem
 * ```
 */
export const Sticky = forwardRef<StickyRefApi, StickyProps>((props, ref) => {
  const scollRef = useRef<HTMLDivElement>(null);
  const activeKeyRef = useRef<string>();
  const behaviorRef = useRef<string>('');
  const [activeKey, setActiveKey] = useState<string>();

  useEffectCustom(() => {
    onScroll();
  }, []);

  useImperativeHandle(ref, () => {
    return {
      scrollToByKey: (stickyKey, behavior) => {
        if (!scollRef.current) return;
        if (activeKey === stickyKey) return;
        const childrens = scollRef.current.children;
        for (let i = 0; i < childrens.length; i++) {
          const panel = childrens.item(i) as HTMLElement;
          if (!panel) continue;
          const panelIndex = panel.dataset['index'];
          if (!panelIndex) continue;
          if (panelIndex === stickyKey) {
            behaviorRef.current = 'click';
            setActiveKey(stickyKey);
            scollRef.current?.scrollTo({
              top: panel.offsetTop,
              behavior,
            });
          }
        }
      },
      scrollTo: (top: number, behavior?: ScrollBehavior) => {
        scollRef.current?.scrollTo({
          top: top,
          behavior,
        });
      },
    };
  });

  const onScroll = hooks.useThrottleCallback((event?) => {
    event && props.onScroll?.(event);
    if (behaviorRef.current === 'click') return;
    const scollBody = scollRef.current;
    if (!scollBody) return;
    const elementList = scollBody.querySelectorAll('.v-sticky-item-title');
    if (elementList.length === 0) return;
    const scrollTop = scollBody.scrollTop;
    for (let i = 0; i < elementList.length; i++) {
      const panel = elementList.item(i) as HTMLElement;
      if (!panel) continue;
      const panelIndex = panel.dataset['index'];
      if (!panelIndex) continue;
      if (Math.round(panel.offsetTop) === Math.round(scrollTop)) {
        if (activeKeyRef.current === panelIndex) return;
        activeKeyRef.current = panelIndex;
        setActiveKey(panelIndex);
        props.onChange?.(panelIndex);
        return;
      }
      if (Math.round(panel.offsetTop) >= Math.round(scrollTop) && i == 0 && activeKeyRef.current) {
        props.onChange?.(undefined);
        activeKeyRef.current = undefined;
        setActiveKey(undefined);
      }
    }
  });

  const onTouchMove = hooks.useCallbackRef(() => {
    behaviorRef.current = 'scroll';
  });

  return (
    <StickyContext.Provider value={{ activeKey }}>
      {withNativeProps(
        props,
        <NativeScroll
          className="v-sticky"
          height={props.height}
          ref={scollRef}
          onScroll={onScroll}
          onTouchMove={onTouchMove}
        >
          {props.children}
        </NativeScroll>,
      )}
    </StickyContext.Provider>
  );
});
